import { useState, useEffect, useRef, useCallback } from "react";

import { API_BASE_URL } from "../config/api";

/** Audiences supported by GET /api/public/plans */
export const PRICING_AUDIENCE_SLUG = {
  preuni: "pre_university",
  university: "university_student",
};

function normalizeFeatures(features) {
  if (!Array.isArray(features)) return [];

  return features.map((feature) => {
    if (typeof feature === "string") {
      return { text: feature, included: true };
    }

    return {
      text: feature.text ?? feature.name ?? feature.label ?? "",
      included: feature.included !== false,
    };
  });
}

function mapPlan(raw) {
  const monthly = raw.prices?.monthly ?? 0;
  const yearly = raw.prices?.yearly ?? 0;
  const season = raw.prices?.season ?? 0;
  const isFree = monthly === 0;

  return {
    id: raw.id ?? raw.name,
    name: raw.name,
    monthly,
    yearly,
    season,
    seasonEndsOn: raw.season_ends_on || null,
    description: raw.tagline || raw.description || "",
    features: normalizeFeatures(raw.features),
    featured: Boolean(raw.popular),
    cta: raw.cta_label || raw.cta || (isFree ? "Download Free" : `Get ${raw.name}`),
    ctaType: "download",
  };
}

const cache = {};

export function usePricing(audienceKey) {
  const slug = PRICING_AUDIENCE_SLUG[audienceKey];
  const [plans, setPlans] = useState(cache[audienceKey] ?? []);
  const [loading, setLoading] = useState(Boolean(slug && !cache[audienceKey]));
  const [error, setError] = useState(null);
  const abortRef = useRef(null);
  const fetchIdRef = useRef(0);

  const fetchPlans = useCallback(() => {
    if (!slug) {
      setPlans([]);
      setLoading(false);
      setError(null);
      return;
    }

    if (!API_BASE_URL) {
      setPlans([]);
      setLoading(false);
      setError("API is not configured.");
      return;
    }

    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    const fetchId = ++fetchIdRef.current;

    setLoading(true);
    setError(null);

    fetch(`${API_BASE_URL}/public/plans?audience=${slug}`, { signal: controller.signal })
      .then(async (res) => {
        const json = await res.json().catch(() => ({}));

        if (res.status === 503) {
          throw new Error(json.message || "Pricing is temporarily unavailable. Please try again shortly.");
        }

        if (!res.ok) {
          throw new Error(json.message || `Unable to load plans (HTTP ${res.status}).`);
        }

        return json;
      })
      .then((json) => {
        if (fetchId !== fetchIdRef.current) return;

        if (json.success && Array.isArray(json.data) && json.data.length > 0) {
          const mapped = json.data.map(mapPlan);
          cache[audienceKey] = mapped;
          setPlans(mapped);
          setError(null);
        } else {
          setPlans([]);
          setError(json.message || "No pricing plans are available right now.");
        }
        setLoading(false);
      })
      .catch((err) => {
        if (err.name === "AbortError" || fetchId !== fetchIdRef.current) return;
        setPlans([]);
        setError(err.message || "Unable to load pricing plans.");
        setLoading(false);
      });
  }, [audienceKey, slug]);

  useEffect(() => {
    if (!slug) return;

    if (cache[audienceKey]) {
      setPlans(cache[audienceKey]);
      setLoading(false);
      setError(null);
      return;
    }

    fetchPlans();
    return () => abortRef.current?.abort();
  }, [audienceKey, slug, fetchPlans]);

  const refetch = useCallback(() => {
    delete cache[audienceKey];
    fetchPlans();
  }, [audienceKey, fetchPlans]);

  return { plans, loading, error, refetch };
}
